/**
 * Session Supabase Auth du miroir CRM.
 *
 * Volontairement séparée du Google OAuth qui sert au mail : ce sont deux
 * identités distinctes, et le compte propriétaire du miroir
 * (`mailfox.is_owner()`) n'a qu'un seul provider — `email`. Un « Sign in
 * with Google » créerait un AUTRE utilisateur Supabase, que RLS refuserait
 * silencieusement ligne par ligne. D'où le mot de passe.
 *
 * supabase-js persiste la session et rafraîchit le JWT tout seul : la
 * saisie n'a lieu qu'une fois, puis l'app reste connectée.
 */

import { supabase } from "./client";

type Status = "unknown" | "in" | "out";

export const crmSession = $state<{
  status: Status;
  email: string | null;
  busy: boolean;
  error: string | null;
}>({
  status: "unknown",
  email: null,
  busy: false,
  error: null,
});

function apply(email: string | null): void {
  crmSession.status = email ? "in" : "out";
  crmSession.email = email;
}

/** À appeler une fois au boot. Résout la session persistée, puis suit. */
export async function initCrmSession(): Promise<void> {
  try {
    const { data } = await supabase.auth.getSession();
    apply(data.session?.user.email ?? null);
  } catch (e) {
    console.warn("crm: getSession failed:", e);
    apply(null);
  }
  // Couvre le refresh de token, l'expiration et le sign-out d'un autre onglet.
  supabase.auth.onAuthStateChange((_event, session) => {
    apply(session?.user.email ?? null);
  });
}

export async function crmSignIn(email: string, password: string): Promise<void> {
  crmSession.busy = true;
  crmSession.error = null;
  try {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
  } catch (e) {
    crmSession.error = e instanceof Error ? e.message : String(e);
  } finally {
    crmSession.busy = false;
  }
}

export async function crmSignOut(): Promise<void> {
  try {
    await supabase.auth.signOut();
  } catch (e) {
    console.warn("crm: signOut failed:", e);
  }
  apply(null);
}
